import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { painPointApi } from '@/services/skillKnowledgeApi';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Plus, Eye, Edit, Trash2, AlertCircle } from 'lucide-react';
import { formatDate } from '@/utils/dateFormatter';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import type { PainPoint } from '@/types/skillKnowledge';

export const PainPointListPage: React.FC = () => {
  const [painPoints, setPainPoints] = useState<PainPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const { hasPermission } = useAdminAuth();
  const { toast } = useToast();

  const canCreate = hasPermission('create', 'painPoint');
  const canEdit = hasPermission('edit', 'painPoint');
  const canDelete = hasPermission('delete', 'painPoint');

  useEffect(() => {
    fetchPainPoints();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPainPoints depends on page/size from closure
  }, [page, size]);

  const fetchPainPoints = async () => {
    try {
      setIsLoading(true);
      const response = await painPointApi.list({ 
        page, 
        size,
        sortBy: 'name',
        sortDirection: 'asc',
      });
      
      // Debug logging to understand API response structure
      console.log('Pain Points API Response:', {
        contentCount: response.content?.length || 0,
        totalElements: response.page?.totalElements || 0,
        totalPages: response.page?.totalPages || 0,
        hasPageObject: !!response.page,
        responseStructure: response,
      });
      
      setPainPoints(response.content || []);
      
      // Use nested page object structure (correct according to PaginatedResponse type)
      const totalElements = response.page?.totalElements || response.content?.length || 0;
      const totalPages = response.page?.totalPages || Math.ceil(totalElements / size) || 1;
      
      setTotalPages(totalPages);
      setTotalElements(totalElements);
    } catch (error: unknown) {
      console.error('Error fetching pain points:', error);
      setPainPoints([]);
      setTotalPages(0);
      setTotalElements(0);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to load pain points. Please check that the API is available.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this pain point?')) return;

    try {
      await painPointApi.delete(id);
      toast({
        variant: 'success',
        title: 'Success',
        description: 'Pain point deleted successfully',
      });
      fetchPainPoints();
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to delete pain point',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pain Point Management</h1>
          <p className="text-muted-foreground mt-2">Manage all pain points</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/pain-points/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Pain Point
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pain Points ({totalElements})</CardTitle>
          <CardDescription>List of all pain points</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <BrandedLoading message="Loading pain points…" layout="inline" iconSize="sm" />
            </div>
          ) : painPoints.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No pain points found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Summary</TableHead>
                    <TableHead>Required Skills</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {painPoints.map((painPoint) => (
                    <TableRow key={painPoint.id}>
                      <TableCell className="font-medium">{painPoint.name}</TableCell>
                      <TableCell>{painPoint.summary || 'N/A'}</TableCell>
                      <TableCell>{painPoint.requiredSkills?.length || 0}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            painPoint.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}
                        >
                          {painPoint.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(painPoint.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/pain-points/${painPoint.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/pain-points/${painPoint.id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => painPoint.id && handleDelete(painPoint.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {(totalPages > 1 || totalElements > size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages || Math.ceil(totalElements / size) || 1} ({totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const maxPage = totalPages - 1 || Math.ceil(totalElements / size) - 1 || 0;
                        setPage((p) => Math.min(maxPage, p + 1));
                      }}
                      disabled={page >= (totalPages - 1 || Math.ceil(totalElements / size) - 1 || 0) || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PainPointListPage;

